import React, { useEffect, useRef, useState } from 'react';
import MonacoEditor, { type MonacoEditorProps } from 'react-monaco-editor';

import i18n from '@yandex-int/i18n';
import { editor as globalEditor, KeyCode, KeyMod, languages, MarkerSeverity } from 'client/lib/monaco';
import { GrammarValidationErrorPayload } from 'client/model/grammar';
import * as keyset from 'i18n/IntentsPage';

import styles from './GranetCodeEditor.module.scss';

interface Props {
    code: string;
    onChange: (value: string) => void;
    readOnly?: boolean;
    validationError?: GrammarValidationErrorPayload;
    onSave?: () => void;
    completionProvider?: languages.CompletionItemProvider;
}

const t = i18n(keyset);

export const GranetCodeEditor: React.FC<Props> = ({
    code,
    onChange,
    readOnly,
    validationError,
    onSave,
    completionProvider,
}) => {
    const editorInstanceRef = useRef<globalEditor.IStandaloneCodeEditor | null>(null);
    const [focused, setFocused] = useState(false);

    useEffect(() => {
        if (completionProvider) {
            const disposeObj = languages.registerCompletionItemProvider('granet', completionProvider);

            return () => disposeObj.dispose();
        }
    }, [completionProvider]);

    useEffect(() => {
        const editor = editorInstanceRef.current;

        if (editor) {
            const focusDisposable = editor.onDidFocusEditorText(() => setFocused(true));
            const blurDisposable = editor.onDidBlurEditorText(() => setFocused(false));

            return () => {
                focusDisposable.dispose();
                blurDisposable.dispose();
            };
        }
    }, []);

    useEffect(() => {
        const editor = editorInstanceRef.current;

        if (editor && onSave) {
            const disposable = editor.addAction({
                id: 'save',
                label: t('button-save'),
                keybindings: [KeyMod.CtrlCmd | KeyCode.KeyS],
                run: onSave,
            });

            return () => disposable.dispose();
        }
    }, [onSave]);

    useEffect(() => {
        const editor = editorInstanceRef.current;

        if (editor) {
            if (validationError && validationError.errorCode === 'VALIDATION_ERROR') {
                const { errorBounds, text } = validationError;
                const { lineNumber, charOffset, charCount } = errorBounds;

                const line = lineNumber + 1;
                const column = charOffset + 1;

                globalEditor.setModelMarkers(editor.getModel()!, '', [
                    {
                        startLineNumber: line,
                        startColumn: column,
                        endLineNumber: line,
                        endColumn: column + charCount,
                        message: text,
                        severity: MarkerSeverity.Error,
                    },
                ]);
            } else {
                globalEditor.setModelMarkers(editor.getModel()!, '', []);
            }
        }
    }, [validationError]);

    const options: MonacoEditorProps['options'] = {
        minimap: {
            enabled: false,
        },
        readOnly: Boolean(readOnly),
        scrollBeyondLastLine: false,
        lineNumbersMinChars: 3,
        scrollbar: {
            verticalScrollbarSize: 5,
            horizontalScrollbarSize: focused ? 2 : 0,
            useShadows: false,
        },
        automaticLayout: true,
        wordBasedSuggestions: 'off',
    };

    return (
        <div className={styles.granetCodeEditor}>
            <MonacoEditor
                language="granet"
                theme="vs"
                height="28vh"
                width="100%"
                value={code}
                options={options}
                onChange={onChange}
                editorDidMount={(editor) => {
                    editorInstanceRef.current = editor;
                }}
            />
        </div>
    );
};
